import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getWeekDayEnum, isSameDay, isToday } from '../util/util';

interface DateSelectorProps {
  dateCount: number;
  date: Date;
  setDate: (date: Date) => void;
}

export function DateSelector({
  dateCount,
  date: selectedDate,
  setDate: setSelectedDate,
}: DateSelectorProps): JSX.Element {
  const { t } = useTranslation();

  const [hasDoneInitialScroll, setHasDoneInitialScroll] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const dates = useMemo(
    () =>
      Array.from({ length: dateCount }, (_, index) => {
        const date = new Date(selectedDate);
        date.setDate(date.getDate() - Math.round(dateCount / 2) + 1 + index);
        return date;
      }),
    [dateCount, selectedDate],
  );

  useEffect(() => {
    const observer = new ResizeObserver(scrollToCenter);

    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    scrollToCenter();
  }, [dates, selectedDate, setSelectedDate]);

  // scroll to the selected date
  function scrollToCenter() {
    const currentDateIndex = dates.findIndex((d) => isSameDay(d, selectedDate));

    if (containerRef.current && containerRef.current.offsetWidth > 0 && currentDateIndex !== -1) {
      const element = containerRef.current.children[currentDateIndex] as HTMLElement;
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const elementOffsetLeft = elementRect.left - containerRect.left;

        const containerWidth = containerRef.current.clientWidth;
        const elementWidth = elementRect.width;

        containerRef.current.scrollTo({
          left: elementOffsetLeft + containerRef.current.scrollLeft - containerWidth / 2 + elementWidth / 2,
          behavior: hasDoneInitialScroll ? 'smooth' : 'instant',
        });

        setHasDoneInitialScroll(true);
      }
    }
  }

  return (
    <div className="rounded-box w-full overflow-x-auto snap-x flex" ref={containerRef}>
      {dates.map((date) => {
        const isSelected = isSameDay(selectedDate, date);
        return (
          <div
            key={date.getTime()}
            onClick={() => setSelectedDate(date)}
            className={`snap-start py-1 min-w-[20%] sm:min-w-[11.111111111%] cursor-pointer text-center ${
              isSelected ? 'bg-primary text-primary-content' : isToday(date) ? 'bg-primary/20' : 'bg-base-100'
            }`}
          >
            <div className="text-base-content/70">{t(getWeekDayEnum(date))}</div>
            <div className="font-bold">{date.getDate()}</div>
          </div>
        );
      })}
    </div>
  );
}
